import { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Slider,
  Grid,
  Chip,
  Divider,
  Alert,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Collapse,
  IconButton,
  Tooltip
} from '@mui/material'
import CalculateIcon from '@mui/icons-material/Calculate'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import InfoIcon from '@mui/icons-material/Info'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import ScienceIcon from '@mui/icons-material/Science'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  EQUIPMENT_STATS,
  FINANCIAL_STATS,
  calculateROI,
  formatWithUncertainty
} from '../../services/corpusStatistics'
import MetricExplanationModal from '../MetricExplanationModal'

// Treatment cost per ton (from corpus data, n=23 documented treatments)
const TREATMENT_COST_PER_TON = FINANCIAL_STATS.treatmentCostPerTon.value

export function QuickROICalculator({ compact = false }) {
  const [expanded, setExpanded] = useState(!compact)
  const [modalMetric, setModalMetric] = useState(null)
  const [inputs, setInputs] = useState({
    equipmentType: 'RTU',
    capacityTons: 20,
    annualKwh: 50000,
    electricityRate: 0.12,
    equipmentAge: 8
  })

  // Use real corpus statistics via calculateROI (5-component corpus-validated model)
  const results = useMemo(() => {
    const roi = calculateROI(inputs)

    return {
      treatmentCost: roi.treatmentCost,
      expectedImprovement: roi.improvement.expected,
      stdDev: roi.improvement.stdDev,
      // Energy-only savings (for reference)
      energyOnlySavings: roi.annualSavings.expected,
      // Total annual value (5 components: energy + avoided costs)
      totalAnnualValue: roi.totalAnnualValue || roi.annualSavings.expected,
      totalAvoidedCosts: roi.totalAvoidedCosts || 0,
      valueBreakdown: roi.valueBreakdown || null,
      // Payback uses total value
      paybackMonths: roi.paybackMonths.expected,
      roi5Year: roi.roi5Year,
      floorSavings: roi.annualSavings.floor,
      optimisticSavings: roi.annualSavings.optimistic,
      floorPayback: roi.paybackMonths.floor,
      optimisticPayback: roi.paybackMonths.optimistic,
      ci95: roi.improvement.ci95,
      n: roi.improvement.n,
      dataSource: roi.dataSource,
      modelVersion: roi.modelVersion || 'energy-only'
    }
  }, [inputs])

  const handleChange = (field) => (event) => {
    setInputs(prev => ({
      ...prev,
      [field]: event.target.value
    }))
  }

  return (
    <Card variant="outlined" sx={{ borderColor: 'success.main' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalculateIcon color="success" />
            <Typography variant="h6">Quick ROI Estimator</Typography>
          </Box>
          {compact && (
            <IconButton onClick={() => setExpanded(!expanded)} size="small">
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>

        <Collapse in={expanded}>
          <Grid container spacing={2}>
            {/* Inputs */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Your Equipment
              </Typography>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Equipment Type</InputLabel>
                <Select
                  value={inputs.equipmentType}
                  label="Equipment Type"
                  onChange={handleChange('equipmentType')}
                >
                  {Object.keys(EQUIPMENT_STATS).map(type => (
                    <MenuItem key={type} value={type}>
                      {type} (n={EQUIPMENT_STATS[type].n})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                size="small"
                label="Capacity"
                type="number"
                value={inputs.capacityTons}
                onChange={handleChange('capacityTons')}
                InputProps={{ endAdornment: <InputAdornment position="end">tons</InputAdornment> }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                size="small"
                label="Annual Energy Use"
                type="number"
                value={inputs.annualKwh}
                onChange={handleChange('annualKwh')}
                InputProps={{ endAdornment: <InputAdornment position="end">kWh</InputAdornment> }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                size="small"
                label="Electricity Rate"
                type="number"
                value={inputs.electricityRate}
                onChange={handleChange('electricityRate')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  endAdornment: <InputAdornment position="end">/kWh</InputAdornment>
                }}
                sx={{ mb: 2 }}
              />

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Equipment Age: {inputs.equipmentAge} years
                </Typography>
                <Slider
                  value={inputs.equipmentAge}
                  onChange={(_, v) => setInputs(prev => ({ ...prev, equipmentAge: v }))}
                  min={1}
                  max={25}
                  marks={[
                    { value: 1, label: '1' },
                    { value: 10, label: '10' },
                    { value: 25, label: '25' }
                  ]}
                  valueLabelDisplay="auto"
                />
              </Box>
            </Grid>

            {/* Results */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Estimated Results
              </Typography>

              <Box sx={{ bgcolor: (theme) => theme.palette.mode === 'light' ? 'success.50' : 'rgba(46, 125, 50, 0.15)', p: 2, borderRadius: 2, mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <AttachMoneyIcon fontSize="small" color="success" />
                      <Typography variant="caption" color="text.secondary">Treatment Cost</Typography>
                    </Box>
                    <Typography variant="h6">${results.treatmentCost.toLocaleString()}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <TrendingUpIcon fontSize="small" color="success" />
                      <Typography variant="caption" color="text.secondary">Expected Improvement</Typography>
                    </Box>
                    <Typography variant="h6">
                      {results.expectedImprovement}%
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                        ± {results.stdDev}%
                      </Typography>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      n={results.n} studies
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <AttachMoneyIcon fontSize="small" color="success" />
                      <Typography variant="caption" color="text.secondary">Total Annual Value</Typography>
                      <IconButton size="small" onClick={() => setModalMetric('total_annual_value')} sx={{ p: 0, ml: 0.5 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      </IconButton>
                    </Box>
                    <Typography variant="h6" color="success.main">
                      ${Number(results.totalAnnualValue).toLocaleString()}
                    </Typography>
                    {results.totalAvoidedCosts > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        incl. ${Number(results.totalAvoidedCosts).toLocaleString()} avoided costs
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <AccessTimeIcon fontSize="small" color="success" />
                      <Typography variant="caption" color="text.secondary">True Payback</Typography>
                      <IconButton size="small" onClick={() => setModalMetric('payback_months')} sx={{ p: 0, ml: 0.5 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      </IconButton>
                    </Box>
                    <Typography variant="h6">{results.paybackMonths} months</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Range estimate */}
              <Box sx={{ bgcolor: 'action.hover', p: 1.5, borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Estimated Savings Range:
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Floor (−1σ)</Typography>
                    <Typography variant="body2">${Number(results.floorSavings).toLocaleString()}/yr</Typography>
                    <Typography variant="caption" color="text.secondary">{results.floorPayback} mo payback</Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary">Ceiling (+1σ)</Typography>
                    <Typography variant="body2">${Number(results.optimisticSavings).toLocaleString()}/yr</Typography>
                    <Typography variant="caption" color="text.secondary">{results.optimisticPayback} mo payback</Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Alert severity="info" icon={<ScienceIcon />} sx={{ mt: 2 }}>
            <Typography variant="caption">
              <strong>5-Component Corpus-Validated Model:</strong> Total Annual Value includes direct energy savings
              plus 4 avoided cost categories (degradation, demand, extended runtime, life extension).
              Based on {results.n} PE-certified measurements. See{' '}
              <Box component="span" sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'primary.main' }} onClick={() => setModalMetric('total_annual_value')}>
                metric explanations
              </Box>
              {' '}for corpus sources.
            </Typography>
          </Alert>
        </Collapse>

        {/* Metric Explanation Modal */}
        <MetricExplanationModal
          metric={modalMetric}
          open={Boolean(modalMetric)}
          onClose={() => setModalMetric(null)}
        />
      </CardContent>
    </Card>
  )
}

export default QuickROICalculator
